(() => {
  if (window.ldsShippingProtectionReady) return;
  const seed = document.querySelector('[data-lds-protection-config]');
  if (!seed) return;
  window.ldsShippingProtectionReady = true;
  const confirmedProductId = 10326384181467;
  const productId = Number(seed.dataset.productId) || confirmedProductId;
  const priceMap = JSON.parse(seed.querySelector('script').textContent);
  const root = window.Shopify?.routes?.root || '/';
  const scope = 'lds-shipping-protection';
  const attribute = 'lds_shipping_protection';
  const patchSelectors = ['.hdt-totals__total-value', '[data-cart-subtotal]', '.lds-cart-mobile-bar>strong'];
  const itemSelectors = ['.hdt-cart-page__items tbody', '.hdt-mini-cart__items form'];
  let pendingChoice = null;
  let revision = 0;
  let running = null;
  let timer;
  let scheduledRevision = null;
  let selfReloadRevision = null;
  let lastCart;
  let cartDirty = true;
  let externalRevision = 0;
  let checkoutPending = false;
  let replaySubmitEvent = null;
  let lastGoodsFingerprint = '';
  let failure = '';
  const staleWrite = Symbol('stale-write');
  const bypass = new WeakSet();
  const termsFormIds = new Set(['CartPage-Form', 'CartDrawer-Form']);
  const termsState = new Map();
  let restoringTerms = false;
  const nodes = () => [...document.querySelectorAll('[data-lds-protection]')];
  const termsControls = form => {
    if (!form || !termsFormIds.has(form.id)) return [];
    return [...document.querySelectorAll('input[name="agree_checkbox"]')].filter(input => {
      if (input.type !== 'checkbox') return false;
      const associated = input.form || input.closest?.('form') || document.getElementById(input.getAttribute?.('form') || '');
      return associated === form;
    });
  };
  const rememberTerms = form => {
    const forms = form ? [form] : [...termsFormIds].map(id => document.getElementById(id));
    for (const current of forms) {
      if (!current || !termsFormIds.has(current.id)) continue;
      const controls = termsControls(current);
      if (controls.length && !termsState.has(current.id)) termsState.set(current.id, Boolean(controls[0].checked));
    }
  };
  const clearTermsWithoutControls = () => {
    for (const id of termsFormIds) {
      const form = document.getElementById(id);
      if (!form || !termsControls(form).length) termsState.delete(id);
    }
  };
  const restoreTerms = form => {
    const forms = form ? [form] : [...termsFormIds].map(id => document.getElementById(id));
    for (const current of forms) {
      if (!current || !termsFormIds.has(current.id)) continue;
      const remembered = termsState.get(current.id);
      if (remembered === undefined) continue;
      const controls = termsControls(current);
      if (!controls.length) continue;
      restoringTerms = true;
      try {
        for (const control of controls) {
          control.checked = remembered;
          control.dispatchEvent(new Event('change', {bubbles: true}));
        }
      } finally {
        restoringTerms = false;
      }
    }
  };
  const fees = cart => cart.items.filter(item => item.product_id === productId);
  const goods = cart => cart.items.filter(item => item.product_id !== productId);
  const clearTermsWhenCartHasNoGoods = cart => { if (cart && !goods(cart).length) termsState.clear(); };
  const quote = cart => Math.round(goods(cart).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0) * 3 / 1000) * 10;
  const goodsCount = cart => goods(cart).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const goodsFingerprint = cart => goods(cart).map(item => ({
    key: item.key || '',
    quantity: Number(item.quantity || 0),
    final_price: Number(item.final_price || 0),
    final_line_price: Number(item.final_line_price || 0),
    discount_allocations: item.line_level_discount_allocations || item.discount_allocations || []
  })).sort((a, b) => a.key.localeCompare(b.key)).map(item => JSON.stringify(item)).join('|');
  const enabled = cart => pendingChoice ?? cart.attributes?.[attribute] !== 'off';
  const desired = cart => {
    const choice = enabled(cart) ? 'on' : 'off';
    const amount = quote(cart);
    const wanted = choice === 'on' && cart.currency === 'USD' && goods(cart).length > 0 && Object.prototype.hasOwnProperty.call(priceMap, String(amount)) ? amount : 0;
    return {choice, wanted};
  };
  const format = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency}).format(cents / 100);
  const sections = () => [...new Set([...document.querySelectorAll('hdt-main-cart[section-id],hdt-cart-drawer[section-id]')].map(node => node.getAttribute('section-id')).filter(Boolean))];
  const request = async (path, body) => {
    const response = await fetch(root + path, body ? {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)} : {credentials: 'same-origin', cache: 'no-store'});
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error(data.description || data.message || seed.dataset.error);
    return data;
  };
  const isCartResponse = cart => Boolean(cart && Array.isArray(cart.items) && typeof cart.currency === 'string' && ['item_count', 'total_price', 'total_discount', 'total_weight', 'items_subtotal_price'].every(key => typeof cart[key] === 'number'));
  const feeStateMatches = (cart, wanted) => {
    if (!isCartResponse(cart)) return false;
    const rows = fees(cart);
    return wanted ? rows.length === 1 && rows[0].quantity === 1 && rows[0].variant_id === Number(priceMap[String(wanted)]) && rows[0].final_line_price === wanted : rows.length === 0;
  };
  const stateMatches = (cart, wanted, choice) => feeStateMatches(cart, wanted) && cart.attributes?.[attribute] === choice;
  const readCart = async () => {
    const cart = await request('cart.js');
    if (!isCartResponse(cart)) throw new Error(seed.dataset.error);
    clearTermsWhenCartHasNoGoods(cart);
    return cart;
  };
  const readLatest = async () => {
    let cart;
    for (let attempt = 0; attempt < 6; attempt++) {
      const seen = externalRevision;
      cart = await readCart();
      if (seen === externalRevision) { cartDirty = false; return cart; }
    }
    throw new Error(seed.dataset.error);
  };
  const clearHandledSchedule = seen => {
    if (scheduledRevision !== seen) return;
    clearTimeout(timer);
    timer = undefined;
    scheduledRevision = null;
  };
  const paint = (cart = lastCart) => {
    for (const node of nodes()) {
      const input = node.querySelector('input');
      if (!input) continue;
      input.disabled = !cart || cart.currency !== 'USD';
      input.checked = cart ? (pendingChoice ?? fees(cart).some(item => item.quantity > 0 && item.final_line_price > 0)) && goods(cart).length > 0 && cart.currency === 'USD' : input.checked;
      node.setAttribute('aria-busy', String(Boolean(running)));
      if (cart) {
        node.hidden = goods(cart).length === 0;
        const amount = fees(cart).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
        const price = node.querySelector('[data-protection-price]');
        if (price) price.textContent = format(running ? amount : input.checked ? amount : 0, cart.currency);
      }
      const message = node.querySelector('[data-protection-message]');
      if (message) {
        const amount = cart ? quote(cart) : 0;
        message.textContent = failure || (cart && cart.currency !== 'USD' ? seed.dataset.currencyError : cart && enabled(cart) && amount && !Object.prototype.hasOwnProperty.call(priceMap, String(amount)) ? seed.dataset.rangeError : '');
        message.hidden = !message.textContent;
      }
      const status = node.querySelector('[data-protection-status]');
      if (status) status.textContent = running ? (seed.dataset.updating || '') : '';
    }
    document.documentElement.classList.toggle('lds-protection-working', Boolean(running));
  };
  const completeSections = (cart, ids) => Boolean(cart?.sections && ids.length && ids.every(id => typeof cart.sections[id] === 'string' && cart.sections[id].trim()));
  const sectionNodes = () => [...document.querySelectorAll('hdt-main-cart[section-id],hdt-cart-drawer[section-id]')];
  const rootTotalNeedsRender = cart => {
    const expected = String(cart.total_price);
    return sectionNodes().some(node => (node.getAttribute('data-lds-cart-total') !== null && node.getAttribute('data-lds-cart-total') !== expected) || node.getAttribute('data-count') !== String(goodsCount(cart)));
  };
  const paintRootTotal = cart => {
    const total = String(cart.total_price);
    for (const node of sectionNodes()) {
      node.setAttribute('data-count', String(goodsCount(cart)));
      if (node.getAttribute('data-lds-cart-total') !== null) node.setAttribute('data-lds-cart-total', total);
    }
  };
  const sectionRenderEvents = () => [...new Set([...document.querySelectorAll('hdt-main-cart[section-id],hdt-cart-drawer[section-id]')].map(node => {
    const tag = (node.localName || node.tagName || '').toLowerCase();
    return tag === 'hdt-cart-drawer' ? 'cart:drawer:render' : 'cart:main:render';
  }))];
  const waitForSectionRender = () => {
    const expected = sectionRenderEvents();
    if (!expected.length) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const seen = new Set();
      let timeout;
      const finish = error => {
        for (const name of expected) document.removeEventListener(name, onRender);
        clearTimeout(timeout);
        if (error) reject(error); else resolve();
      };
      const onRender = event => {
        seen.add(event.type);
        if (expected.every(name => seen.has(name))) finish();
      };
      for (const name of expected) document.addEventListener(name, onRender);
      // Never allow checkout to submit stale form rows when rendering fails.
      timeout = setTimeout(() => finish(new Error(seed.dataset.error)), 5000);
    });
  };
  const sectionRoot = (container, id) => {
    const candidates = [...container.querySelectorAll('hdt-main-cart[section-id],hdt-cart-drawer[section-id],[ref="hdt-cart"]')];
    return candidates.find(node => node.getAttribute('section-id') === id) || null;
  };
  const patchSectionTotals = (cart, ids) => {
    const Parser = window.DOMParser || (typeof DOMParser === 'function' ? DOMParser : null);
    if (!Parser) return {ok: false, failed: ids};
    const patches = [];
    for (const id of ids) {
      const html = cart.sections?.[id];
      if (typeof html !== 'string' || !html.trim()) return {ok: false, failed: [id]};
      let parsed;
      try {
        parsed = new Parser().parseFromString(html, 'text/html');
      } catch {
        return {ok: false, failed: [id]};
      }
      const currentRoot = sectionRoot(document, id);
      const freshRoot = parsed && sectionRoot(parsed, id);
      if (!currentRoot || !freshRoot) return {ok: false, failed: [id]};
      const targets = [];
      let matchedTotals = false;
      let matchedItems = false;
      for (const selector of patchSelectors) {
        const current = [...currentRoot.querySelectorAll(selector)];
        const fresh = [...freshRoot.querySelectorAll(selector)];
        if (current.length !== fresh.length) return {ok: false, failed: [id]};
        if (current.length) matchedTotals = true;
        for (let index = 0; index < current.length; index++) targets.push([current[index], fresh[index]]);
      }
      for (const selector of itemSelectors) {
        const current = [...currentRoot.querySelectorAll(selector)];
        const fresh = [...freshRoot.querySelectorAll(selector)];
        if (current.length !== fresh.length) return {ok: false, failed: [id]};
        if (current.length) matchedItems = true;
        for (let index = 0; index < current.length; index++) patches.push({root: current[index], fresh: fresh[index], rows: true});
      }
      if (!matchedTotals || !matchedItems) return {ok: false, failed: [id]};
      patches.push({root: currentRoot, targets});
    }
    const count = String(goodsCount(cart));
    for (const patch of patches) {
      if (patch.rows) {
        patch.root.innerHTML = patch.fresh.innerHTML;
        continue;
      }
      for (const [current, fresh] of patch.targets) current.innerHTML = fresh.innerHTML;
      patch.root.setAttribute('data-count', count);
      if (patch.root.getAttribute('data-lds-cart-total') !== null) patch.root.setAttribute('data-lds-cart-total', String(cart.total_price));
    }
    return {ok: true, failed: []};
  };
  const render = async cart => {
    const ids = sections();
    let next = cart;
    if (lastCart && ids.length && goodsFingerprint(cart) === lastGoodsFingerprint && completeSections(cart, ids)) {
      const patched = patchSectionTotals(cart, ids);
      if (patched.ok) {
        document.dispatchEvent(new CustomEvent('lds:protection:updated', {detail: {cartData: cart}}));
        return;
      }
      next = {...cart, sections: {...cart.sections}};
      for (const id of patched.failed) next.sections[id] = null;
    }
    if (next.sections && ids.every(id => next.sections[id])) {
      const rendered = waitForSectionRender();
      document.dispatchEvent(new CustomEvent('cart:update', {detail: {cartData: next, source: scope, actionAfterATC: 'no_action'}}));
      await rendered;
    } else {
      const rendered = waitForSectionRender();
      selfReloadRevision = revision;
      document.dispatchEvent(new CustomEvent('cart:reload', {detail: {source: scope}}));
      await rendered;
    }
    paintRootTotal(next);
  };
  const update = async (cart, wanted, choice, seen = revision) => {
    const rows = fees(cart);
    const wantedId = Number(priceMap[String(wanted)] || 0);
    const keep = rows.find(item => item.variant_id === wantedId);
    const updates = {};
    for (const item of rows) {
      if (!wanted || item !== keep || item.quantity !== 1) updates[item.key] = item === keep && wanted ? 1 : 0;
    }
    if (wanted && !keep) updates[wantedId] = 1;
    const body = {attributes: {[attribute]: choice}, sections: sections(), sections_url: window.location.pathname};
    if (Object.keys(updates).length) body.updates = updates;
    if (seen !== revision) return staleWrite;
    try {
      return await request('cart/update.js', body);
    } catch (error) {
      cartDirty = true;
      throw error;
    }
  };
  const reconcile = async (forceRender = false) => {
    let cart = pendingChoice !== null && !cartDirty && isCartResponse(lastCart) ? lastCart : await readLatest();
    // A fresh empty cart has no persistent token yet. Writing its attributes
    // can race the first add-to-cart and replace that new cart's cookie.
    // There is no fee or choice to reconcile until a line exists.
    if (!cart.items.length) {
      if (forceRender) {
        const seen = revision;
        await render(cart);
        if (seen !== revision) {
          cart = await readLatest();
          if (cart.items.length) return reconcile(true);
        }
      }
      pendingChoice = null;
      lastCart = cart;
      lastGoodsFingerprint = goodsFingerprint(cart);
      paint();
      clearHandledSchedule(revision);
      return cart;
    }
    for (let attempt = 0; attempt < 6; attempt++) {
      const seen = revision;
      const seenExternal = externalRevision;
      const nextCart = observed => isCartResponse(observed) && seenExternal === externalRevision ? Promise.resolve(observed) : readLatest();
      const {choice, wanted} = desired(cart);
      const correct = stateMatches(cart, wanted, choice);
      if (correct && seen === revision) {
        if (forceRender || rootTotalNeedsRender(cart)) {
          await render(cart);
          if (seen !== revision) {
            cart = await nextCart(cart);
            continue;
          }
        }
        pendingChoice = null;
        lastCart = cart;
        lastGoodsFingerprint = goodsFingerprint(cart);
        paint();
        clearHandledSchedule(seen);
        return cart;
      }
      const changed = await update(cart, wanted, choice, seen);
      // Toggle-only changes can reuse the confirmed mutation response. External
      // cart changes require a fresh read before the next write or render.
      if (changed === staleWrite || seen !== revision) {
        cart = await nextCart(changed);
        continue;
      }
      let observed = changed;
      if (!stateMatches(observed, wanted, choice)) {
        // Shopify's update endpoint returns a full cart. A malformed or
        // unexpected response gets one safe reread before the fallback.
        observed = await readLatest();
        if (seen !== revision) {
          cart = observed;
          continue;
        }
        if (!stateMatches(observed, wanted, choice)) {
          const fallbackSeen = revision;
          const fallback = await update(observed, 0, 'off', fallbackSeen);
          if (fallback === staleWrite || fallbackSeen !== revision) {
            cart = await readLatest();
            continue;
          }
          if (isCartResponse(fallback)) {
            lastCart = fallback;
            lastGoodsFingerprint = goodsFingerprint(fallback);
            await render(fallback);
            if (fallbackSeen !== revision) {
              cart = await readLatest();
              continue;
            }
          }
          pendingChoice = false;
          throw new Error(seed.dataset.discountError);
        }
      }
      if (seen !== revision) {
        cart = await nextCart(observed);
        continue;
      }
      const latest = desired(observed);
      if (latest.choice !== choice || latest.wanted !== wanted) {
        clearTermsWhenCartHasNoGoods(observed);
        cart = observed;
        continue;
      }
      await render(observed);
      if (seen !== revision) {
        cart = await nextCart(observed);
        continue;
      }
      pendingChoice = null;
      lastCart = observed;
      lastGoodsFingerprint = goodsFingerprint(observed);
      paint();
      clearHandledSchedule(seen);
      return observed;
    }
    throw new Error(seed.dataset.error);
  };
  const sync = () => {
    rememberTerms();
    selfReloadRevision = null;
    clearTimeout(timer);
    timer = undefined;
    scheduledRevision = null;
    if (running) {
      paint();
      return running;
    }
    failure = '';
    const syncRevision = revision;
    let retryAfterRecovery = false;
    let recoverySucceeded = false;
    running = Promise.resolve().then(reconcile).catch(async error => {
      cartDirty = true;
      failure = error.message === seed.dataset.discountError ? seed.dataset.discountError : seed.dataset.error;
      // A toggle can arrive after the write failed but before the recovery
      // read settles. Keep that newer choice and queue it after this run.
      if (revision === syncRevision) pendingChoice = null;
      try {
        const actual = await readLatest();
        if (revision === syncRevision) {
          pendingChoice = null;
          await render(actual);
        }
        lastCart = actual;
        lastGoodsFingerprint = goodsFingerprint(actual);
        paint(actual);
        // Intent can also change while the recovery DOM render is awaited.
        if (revision !== syncRevision) {
          await reconcile(true);
          recoverySucceeded = true;
          failure = '';
        }
      } catch {
        retryAfterRecovery = revision !== syncRevision && !recoverySucceeded;
        paint();
      }
      if (!recoverySucceeded) throw error;
    }).finally(() => {
      running = null;
      paint();
      if (retryAfterRecovery && revision !== syncRevision) schedule();
    });
    paint();
    return running;
  };
  const schedule = () => {
    clearTimeout(timer);
    scheduledRevision = revision;
    timer = setTimeout(() => {
      timer = undefined;
      scheduledRevision = null;
      sync().catch(() => {});
    }, 120);
  };
  document.addEventListener('change', event => {
    const target = event.target;
    const input = target?.closest?.('[data-protection-toggle]');
    if (input) {
      pendingChoice = input.checked;
      revision++;
      paint();
      sync().catch(() => {});
      return;
    }
    if (restoringTerms || target?.type !== 'checkbox' || target.name !== 'agree_checkbox') return;
    const form = target.form || target.closest?.('form') || document.getElementById(target.getAttribute?.('form') || '');
    if (form && termsFormIds.has(form.id)) termsState.set(form.id, Boolean(target.checked));
  });
  document.addEventListener('cart:update', event => {
    if (event.detail?.source === scope) {
      clearTermsWhenCartHasNoGoods(event.detail?.cartData);
      return;
    }
    selfReloadRevision = null;
    externalRevision++;
    cartDirty = true;
    const cart = event.detail?.cartData;
    if (isCartResponse(cart)) {
      lastCart = cart;
      lastGoodsFingerprint = goodsFingerprint(cart);
      clearTermsWhenCartHasNoGoods(cart);
      paint();
    }
    revision++;
    schedule();
  });
  const handleSectionRender = () => {
    clearTermsWithoutControls();
    rememberTerms();
    restoreTerms();
    paint();
    const ownReload = selfReloadRevision === revision;
    if (ownReload) selfReloadRevision = null;
    if (!ownReload && !running) { cartDirty = true; externalRevision++; schedule(); }
  };
  document.addEventListener('cart:drawer:render', handleSectionRender);
  document.addEventListener('cart:main:render', handleSectionRender);
  document.addEventListener('discount:update', () => {selfReloadRevision = null; cartDirty = true; externalRevision++; revision++; schedule();});
  document.addEventListener('quantity-selector:update', () => {selfReloadRevision = null; cartDirty = true; externalRevision++; revision++; schedule();});
  window.addEventListener('pageshow', () => { checkoutPending = false; cartDirty = true; externalRevision++; revision++; schedule(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { cartDirty = true; externalRevision++; revision++; } });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (bypass.has(form)) {bypass.delete(form); replaySubmitEvent = event; return;}
    if (event.submitter?.name !== 'checkout' || !nodes().length) return;
    rememberTerms(form);
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    const formId = form.id;
    const clicked = event.submitter;
    const original = {disabled: clicked.disabled, label: clicked.getAttribute('aria-label'), busy: clicked.getAttribute('aria-busy')};
    const restoreButton = () => {
      clicked.disabled = original.disabled;
      clicked.removeAttribute('data-lds-checkout-waiting');
      for (const [name, value] of [['aria-label', original.label], ['aria-busy', original.busy]]) {
        if (value === null) clicked.removeAttribute(name); else clicked.setAttribute(name, value);
      }
    };
    clicked.disabled = true;
    clicked.setAttribute('data-lds-checkout-waiting', '');
    clicked.setAttribute('aria-label', 'Updating total. Please wait.');
    clicked.setAttribute('aria-busy', 'true');
    let submitted = false;
    try {
      await sync();
      const current = document.getElementById(formId);
      const submitter = [...document.querySelectorAll('[name="checkout"]')].find(button => button.form === current);
      if (!current || !submitter) return;
      restoreTerms(current);
      if (termsControls(current).some(control => !control.checked)) return;
      if (!current.reportValidity()) return;
      restoreButton();
      replaySubmitEvent = null;
      bypass.add(current);
      current.requestSubmit(submitter);
      submitted = Boolean(replaySubmitEvent && !replaySubmitEvent.defaultPrevented);
      bypass.delete(current);
    } catch {paint();}
    finally { restoreButton(); if (!submitted) checkoutPending = false; }
  }, true);
  if (nodes().length) schedule();
})();

(() => {
  if (window.ldsCartTermsFeedbackReady) return;
  window.ldsCartTermsFeedbackReady = true;
  const formIds = new Set(['CartPage-Form', 'CartDrawer-Form']);
  const controls = form => [...document.querySelectorAll('input[name="agree_checkbox"]')].filter(input => input.form === form);
  const clear = input => {
    const block = input.closest('hdt-block-required');
    if (!block) return;
    block.classList.remove('lds-cart-terms-error', 'lds-cart-terms-nudge');
    input.removeAttribute('aria-invalid');
    const message = block.querySelector('[data-lds-terms-error]');
    if (message) message.hidden = true;
  };
  const show = input => {
    const block = input.closest('hdt-block-required');
    if (!block) return;
    let message = block.querySelector('[data-lds-terms-error]');
    if (!message) {
      message = document.createElement('p');
      message.id = input.id + '-error';
      message.dataset.ldsTermsError = '';
      message.setAttribute('role', 'alert');
      block.append(message);
      const ids = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      ids.add(message.id);
      input.setAttribute('aria-describedby', [...ids].join(' '));
    }
    message.hidden = false;
    message.textContent = 'Please agree to the Terms and Conditions to continue.';
    input.setAttribute('aria-invalid', 'true');
    block.classList.add('lds-cart-terms-error');
    block.classList.remove('lds-cart-terms-nudge');
    void block.offsetWidth;
    block.classList.add('lds-cart-terms-nudge');
    input.focus({preventScroll: true});
    const box = block.getBoundingClientRect();
    if (box.top < 100 || box.bottom > window.innerHeight) block.scrollIntoView({block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
  };
  const guard = (event, form) => {
    if (!form || !formIds.has(form.id)) return;
    const unchecked = controls(form).find(input => !input.checked);
    if (!unchecked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    show(unchecked);
  };
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[name="checkout"]');
    if (button) guard(event, button.form);
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') guard(event, event.target);
  }, true);
  document.addEventListener('invalid', event => {
    if (event.target.name === 'agree_checkbox' && formIds.has(event.target.form?.id)) {
      event.preventDefault();
      show(event.target);
    }
  }, true);
  document.addEventListener('change', event => {
    if (event.target.name === 'agree_checkbox' && event.target.checked) clear(event.target);
  });
})();
